import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import client from '../../sanity/SanityClient';
import './DetalleLibros.css';

const nf = new Intl.NumberFormat('es-AR', {
  style: 'currency',
  currency: 'ARS',
  maximumFractionDigits: 0,
});

// Datos básicos de contacto/WA
const STORE = {
  name: 'Taller Juani Pavlovsky',
  addressLine: 'Juncal 2064, CABA',
  openHours: 'Lun a Vie 9–17 hs',
  mapsUrl: 'https://www.google.com/maps?q=Juncal+2064,+CABA',
  whatsapp: '+54 9 11 6003 5550',
};
const buildWALink = (number, text) =>
  `https://wa.me/${number.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

function DrawerPortal({ open, onClose, children }) {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = open ? 'hidden' : prev || '';
    return () => { document.body.style.overflow = prev || ''; };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className="jpvDrawer jpvDrawer--open" aria-hidden={!open}>
      <div className="jpvDrawer__backdrop" onClick={onClose} />
      <aside className="jpvDrawer__panel" role="dialog" aria-modal="true">
        {children}
      </aside>
    </div>,
    document.body
  );
}

// normalizador + zonas de envío
function normalize(str = '') {
  return String(str).toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');
}
function getShippingZone(provinciaInput = '', localidadInput = '') {
  const provRaw = (provinciaInput || '').trim();
  if (!provRaw) return null;
  const prov = normalize(provinciaInput);
  const loc  = normalize(localidadInput || '');
  const isCABA =
    prov.includes('caba') || prov.includes('ciudad autonoma') || prov.includes('ciudad autónoma') ||
    loc.includes('caba') || loc.includes('capital federal') || loc === 'capital';
  if (isCABA) return { key: 'CABA', cost: 15000 };
  const isPBA = prov.includes('buenos aires') || prov === 'pba';
  if (isPBA) return { key: 'AMBA', cost: 20000 };
  return { key: 'PROV', cost: 25000 };
}

const DetalleLibros = () => {
  const { id } = useParams();

  // datos
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const [mainUrl, setMainUrl] = useState('');
  const [thumbs, setThumbs] = useState([]);

  // cantidad
  const [qty, setQty] = useState(1);

  // compra
  const [showCheckout, setShowCheckout] = useState(false);
  const [payMethod, setPayMethod] = useState('mp'); // 'mp' | 'card' | 'cash'
  const [creatingPref, setCreatingPref] = useState(false);
  const [prefError, setPrefError] = useState('');

  // cupón
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponMsg, setCouponMsg] = useState('');

  // entrega
  const [delivery, setDelivery] = useState('retiro'); // 'retiro' | 'envio'
  const [cp, setCp] = useState('');
  const [address, setAddress] = useState({ calle:'', numero:'', piso:'', dpto:'', localidad:'', provincia:'' });
  const [phone, setPhone] = useState('');

  const zoneInfo = useMemo(() => getShippingZone(address.provincia, address.localidad), [address.provincia, address.localidad]);
  const shippingCost = useMemo(() => (delivery === 'envio' && zoneInfo ? zoneInfo.cost : 0), [delivery, zoneInfo]);

  // padding compacto para sticky
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 900px)').matches : false
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const h = (e) => setIsMobile(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', h); else mq.addListener(h);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', h); else mq.removeListener(h); };
  }, []);
  const bodyPadBottom = isMobile
    ? 'calc(28px + env(safe-area-inset-bottom))'
    : 'calc(10px + env(safe-area-inset-bottom))';

  // cargar doc (incluye stock e imágenes reales)
  useEffect(() => {
    const run = async () => {
      setLoading(true); setErr('');
      try {
        const query = `
          *[_type=="libro" && _id==$id][0]{
            _id, titulo, descripcion, precio, stock,
            "previewUrl": coalesce(
              previewImage.asset->url,
              detalleImage.asset->url,
              detalleImage2.asset->url,
              detalleImage3.asset->url
            ),
            "thumb1": detalleImage.asset->url,
            "thumb2": detalleImage2.asset->url,
            "thumb3": detalleImage3.asset->url
          }`;
        const data = await client.fetch(query, { id });
        if (!data) { setErr('No se encontró el libro.'); setDoc(null); setLoading(false); return; }
        setDoc(data);
        setMainUrl(data.previewUrl || '');
        setThumbs([data.thumb1, data.thumb2, data.thumb3].filter(Boolean));
      } catch (e) {
        console.error(e);
        setErr('Error al cargar el libro');
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [id]);

  const isInStock = (doc?.stock ?? true) === true; // si el campo no existe, lo tratamos como en stock
  const price = Number(doc?.precio || 0);
  const subtotal = price * qty;
  const couponDiscount = appliedCoupon ? Math.round(subtotal * (appliedCoupon.percent / 100)) : 0;
  const total = Math.max(0, subtotal - couponDiscount) + (isInStock ? shippingCost : 0);

  const handleSwapIndex = (idx) => {
    setThumbs(prev => {
      if (idx < 0 || idx >= prev.length) return prev;
      const next = [...prev];
      const clicked = next[idx];
      next[idx] = mainUrl;
      setMainUrl(clicked);
      return next;
    });
  };

  // cupón
  const applyCoupon = async () => {
    const code = (couponInput || '').trim();
    setCouponMsg('');
    if (!code) return;
    try {
      const resp = await fetch('/api/discount/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code })
      });
      const data = await resp.json();
      if (!resp.ok || !data?.valid) throw new Error(data?.message || 'Cupón inválido');
      setAppliedCoupon({ code: data.code, percent: Number(data.percent || 0) });
      setCouponMsg(`Cupón aplicado: ${data.code} (-${data.percent}%)`);
    } catch (e) {
      setAppliedCoupon(null);
      setCouponMsg(e?.message || 'Cupón inválido');
    }
  };
  const removeCoupon = () => { setAppliedCoupon(null); setCouponInput(''); setCouponMsg('Cupón quitado'); };

  // pagar
  const finalizarCompra = async () => {
    try {
      setCreatingPref(true);
      setPrefError('');

      if (delivery === 'envio') {
        const ok = cp && address.calle && address.numero && address.localidad && address.provincia && zoneInfo && (phone || '').trim().length >= 8;
        if (!ok) throw new Error('Completá los datos de envío');
      }
      if (total <= 0) throw new Error('transaction_amount must be positive');

      const resp = await fetch('/api/mp/create-preference', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: `${doc?.titulo || 'Libro'} x${qty}`,
          quantity: 1,
          unit_price: Math.round(total),
          buyerEmail: localStorage.getItem('authEmail') || '',
          picture_url: mainUrl || '',
          payMethod,
          delivery,
          shippingCost: Math.round(Number(shippingCost || 0)),
          address: { cp, telefono: phone, ...address },
          metadata: {
            kind: 'libro',
            id: doc?._id || null,
            unit: Math.round(Number(price || 0)),
            qty,
            coupon: appliedCoupon || null
          }
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.message || 'Fallo al crear preference');
      const urlToPay = data.init_point || data.sandbox_init_point;
      if (!urlToPay) throw new Error('Respuesta sin init_point');
      window.location.assign(urlToPay);
    } catch (e) {
      console.error(e);
      setPrefError(e?.message || 'No se pudo iniciar el pago');
    } finally {
      setCreatingPref(false);
    }
  };

  if (loading) return <div className="detallelibros-wrapper"><p className="detallelibros-info">Cargando…</p></div>;
  if (err)      return <div className="detallelibros-wrapper"><p className="detallelibros-info">{err}</p></div>;
  if (!doc) return null;

  // WhatsApp (cuando no hay stock)
  const waMsg = `Hola Juani. Quisiera consultarte por la disponibilidad del libro "${doc.titulo || 'Libro'}".`;
  const waHref = buildWALink(STORE.whatsapp, waMsg);

  return (
    <div className="detallelibros-wrapper" onContextMenu={(e) => e.preventDefault()}>
      <div className="detallelibros-grid">
        {/* Thumbs */}
        <div className="libro-thumb-col">
          <div className="libro-thumbs">
            {[mainUrl, ...thumbs].filter(Boolean).slice(1).map((url, idx) => (
              <button
                key={idx}
                type="button"
                className="libro-thumb-wrap"
                onClick={() => handleSwapIndex(idx)}
                title="Ver otra imagen"
              >
                <img src={url} alt={`Miniatura ${idx+1}`} className="libro-thumb-img" draggable="false" onDragStart={(e)=>e.preventDefault()} />
                <span className="libro-thumb-overlay" />
                {idx === 0 && <span className="libro-thumb-badge">+{thumbs.length}</span>}
              </button>
            ))}
            {thumbs.length === 0 && <div className="libro-thumb-placeholder">Sin imágenes</div>}
          </div>
        </div>

        {/* Main */}
        <div className="libro-main-col">
          {mainUrl ? (
            <button type="button" className="libro-main-img-wrap" title="Ampliar">
              <img src={mainUrl} alt={doc.titulo || 'Libro'} className="libro-main-img" draggable="false" onDragStart={(e)=>e.preventDefault()} />
            </button>
          ) : (
            <div className="libro-main-placeholder">Sin imagen</div>
          )}
        </div>

        {/* Info */}
        <div className="libro-info-col">
          <div className="libro-title-row">
            <h1 className="detallelibros-title">{doc.titulo || 'Libro'}</h1>
          </div>

          {/* Precio / estado */}
          <div className="libro-price-row">
            <span className="libro-price-label">Precio</span>
            {isInStock ? (
              <span className="libro-price-value">{nf.format(price)}</span>
            ) : (
              <span className="libro-price-value" style={{ color: '#c21414', fontWeight: 700 }}>Out Of Stock</span>
            )}
          </div>

          {/* CTA según stock */}
          {isInStock ? (
            <div className="libro-actions">
              <button className="libro-btn-buy" onClick={() => { setPrefError(''); setShowCheckout(true); }}>
                Comprar
              </button>
            </div>
          ) : (
            <div className="price-contact" style={{ margin: '.25rem 0 1rem' }}>
              <small>Contactar para más detalles </small>
              <a className="wa-cta" href={waHref} target="_blank" rel="noreferrer"><strong>Escribir por WhatsApp</strong></a>
            </div>
          )}

          {doc.descripcion && (
            <div className="libro-desc">
              <h3>Descripción</h3>
              <p className="libro-desc-text">{doc.descripcion}</p>
            </div>
          )}

          {/* Acordeones (opcionalmente ya en tu CSS) */}
          <details className="acc">
            <summary>Medios de pago</summary>
            <div className="acc-body">Mercado Pago <br/> Tarjetas <br/> Efectivo</div>
          </details>
          <details className="acc">
            <summary>Medios de envío</summary>
            <div className="acc-body">Retiro en tienda o envío a domicilio en CABA/AMBA/Interior.</div>
          </details>
          <details className="acc">
            <summary>Cambios y devoluciones</summary>
            <div className="acc-body">Ver términos y condiciones en la sección legal.</div>
          </details>
        </div>
      </div>

      {/* Drawer SOLO si hay stock */}
      {isInStock && (
        <DrawerPortal open={showCheckout} onClose={() => setShowCheckout(false)}>
          <div className="jpvDrawer__header">
            <strong>Compra</strong>
            <button className="jpvDrawer__close" onClick={() => setShowCheckout(false)} aria-label="Cerrar">×</button>
          </div>

          {/* Body con padding inferior dinámico */}
          <div className="jpvDrawer__body" style={{ paddingBottom: bodyPadBottom }}>
            {/* Mini resumen con contador */}
            <div className="mini-item">
              <img src={mainUrl || ''} alt="thumb" />
              <div className="mini-info">
                <div className="mini-title">{doc.titulo || 'Libro'}</div>
                <div className="mini-sub">Unidad: {nf.format(price)}</div>
                <div className="mini-price">Subtotal: {nf.format(subtotal)}</div>
              </div>
              <div className="qty">
                <button onClick={() => setQty(q => Math.max(1, q - 1))} aria-label="menos">−</button>
                <span>{qty}</span>
                <button onClick={() => setQty(q => q + 1)} aria-label="más">+</button>
              </div>
            </div>

            {/* Entrega */}
            <div className="section">
              <div className="section-title">Entrega</div>
              <div className="toggle-group" role="tablist" aria-label="Elegir entrega">
                <button type="button" className={`size-btn ${delivery === 'retiro' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'retiro'} onClick={() => setDelivery('retiro')}>Retiro en tienda (sin costo)</button>
                <button type="button" className={`size-btn ${delivery === 'envio' ? 'active' : ''}`} role="tab" aria-selected={delivery === 'envio'} onClick={() => setDelivery('envio')}>Envío a domicilio</button>
              </div>

              {delivery === 'retiro' ? (
                <div className="card card-soft">
                  <div className="card-title">{STORE.name}</div>
                  <div className="card-sub">{STORE.addressLine}</div>
                  <div className="card-sub">Horarios: {STORE.openHours}</div>
                  <div className="card-note">⏱ Se puede retirar 9 días después de la compra.</div>
                  <a className="link-arrow" href={STORE.mapsUrl} target="_blank" rel="noreferrer">Ver ubicación en Google Maps</a>
                </div>
              ) : (
                <div className="card card-soft">
                  <div className="shipping-form">
                    <div className="row inline"><label>Código postal</label><input value={cp} onChange={e => setCp(e.target.value)} placeholder="Ej: 1425" /></div>
                    <div className="row two">
                      <div><label>Calle</label><input value={address.calle} onChange={e => setAddress(a => ({ ...a, calle: e.target.value }))} /></div>
                      <div><label>Número</label><input value={address.numero} onChange={e => setAddress(a => ({ ...a, numero: e.target.value }))} /></div>
                    </div>
                    <div className="row two">
                      <div><label>Piso</label><input value={address.piso} onChange={e => setAddress(a => ({ ...a, piso: e.target.value }))} /></div>
                      <div><label>Dpto</label><input value={address.dpto} onChange={e => setAddress(a => ({ ...a, dpto: e.target.value }))} /></div>
                    </div>
                    <div className="row two">
                      <div><label>Localidad</label><input value={address.localidad} onChange={e => setAddress(a => ({ ...a, localidad: e.target.value }))} /></div>
                      <div><label>Provincia</label><input value={address.provincia} onChange={e => setAddress(a => ({ ...a, provincia: e.target.value }))} /></div>
                    </div>
                    <div className="row inline"><label>Teléfono</label><input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="Ej: 11 2345-6789" /></div>
                  </div>

                  <div className="sep" />
                  <div className="row-between"><span>Costo de envío</span><strong>{zoneInfo ? nf.format(shippingCost) : '—'}</strong></div>
                  <small className="muted">{zoneInfo ? 'El envío se suma a los 9 días de producción.' : 'Ingresá provincia y localidad para calcular el costo.'}</small>
                </div>
              )}
            </div>

            {/* Pago */}
            <div className="section">
              <div className="section-title">Pago</div>
              <div className="toggle-group" role="tablist" aria-label="Elegir forma de pago">
                <button type="button" className={`size-btn ${payMethod === 'mp' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'mp'} onClick={() => setPayMethod('mp')}>Mercado Pago (billetera)</button>
                <button type="button" className={`size-btn ${payMethod === 'card' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'card'} onClick={() => setPayMethod('card')}>Tarjeta</button>
                <button type="button" className={`size-btn ${payMethod === 'cash' ? 'active' : ''}`} role="tab" aria-selected={payMethod === 'cash'} onClick={() => setPayMethod('cash')}>Efectivo</button>
              </div>

              {/* cupón */}
              <div className="coupon-box">
                <label htmlFor="coupon">Código de descuento</label>
                <div className="coupon-row">
                  <input id="coupon" value={couponInput} onChange={(e) => setCouponInput(e.target.value)} className="input" />
                  <button type="button" onClick={applyCoupon}>Aplicar</button>
                  {appliedCoupon && <button type="button" className="link-btn" onClick={removeCoupon}>Quitar cupón</button>}
                </div>
                {couponMsg && <small className="muted">{couponMsg}</small>}
                {appliedCoupon && <small className="muted">Aplicado: {appliedCoupon.code} (−{appliedCoupon.percent}%)</small>}
              </div>
            </div>

            {/* Totales */}
            <div className="totals">
              <div><span>Subtotal</span><span>{nf.format(subtotal)}</span></div>
              {appliedCoupon && (<div><span>Descuento cupón ({appliedCoupon.percent}%)</span><span>-{nf.format(couponDiscount)}</span></div>)}
              <div><span>Envío</span><span>{delivery === 'envio' ? (zoneInfo ? nf.format(shippingCost) : '—') : 'Sin costo'}</span></div>
              <div className="grand"><span>Total</span><span>{nf.format(total)}</span></div>
            </div>

            {prefError && <div className="error-box">{prefError}</div>}
          </div>

          {/* Footer sticky sin placa blanca */}
          <div
            className="drawer-sticky"
            style={{
              position: 'sticky',
              bottom: 0,
              zIndex: 5,
              padding: '4px 12px',
              paddingBottom: 'calc(4px + env(safe-area-inset-bottom))',
            }}
          >
            <button
              className="btn-primary"
              onClick={finalizarCompra}
              disabled={
                creatingPref ||
                (delivery === 'envio' &&
                  (!cp || !address.calle || !address.numero || !address.localidad || !address.provincia || !zoneInfo || phone.trim().length < 8))
              }
            >
              {creatingPref ? 'Creando pago…' : 'Finalizar compra'}
            </button>
          </div>
        </DrawerPortal>
      )}
    </div>
  );
};

export default DetalleLibros;
